import { Block } from '@components/core-ui/Block';
import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { Input } from '@components/core-ui/form-elements/Input';
import LoadingIcon from '@components/icons/Loading.svg';
import { KeyTermsList } from '@components/KeyTermsList';
import {
  getKeyTermsValidationError,
  KEY_TERMS_SEPARATOR_REGEXP,
  normalizeKeyTerms,
} from '@helpers/keyTerms/normalizeKeyTerms';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { JobDtoAttachment, JobsService } from 'app/API';
import { jobsKeys } from 'app/queries/jobs/jobsKeys';
import { type ChangeEvent, type KeyboardEvent, useState } from 'react';
import toast from 'react-hot-toast';

const splitKeyTerms = (value: string) => {
  return value
    .split(KEY_TERMS_SEPARATOR_REGEXP)
    .map((keyTerm) => keyTerm.trim())
    .filter((keyTerm) => !!keyTerm);
};

export const KeyTermsBlock = ({
  noticeExtractionRunning,
  attachments,
}: {
  noticeExtractionRunning: boolean;
  attachments: JobDtoAttachment[];
}) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { workspace } = useAuthContext();
  const { form, setFormItemValue, ensureDraftJob, setJob } = usePageContext();
  const [inputValue, setInputValue] = useState('');
  const importKeyTermsMutation = useMutation({
    mutationFn: (request: Parameters<typeof JobsService.importKeyTermsFromExtractedNotices>[0]) =>
      call(() => JobsService.importKeyTermsFromExtractedNotices(request), {
        dontRedirectOnError: true,
        onError: (message) => toast.error(message),
      }),
    onSuccess: (result) => {
      if (!result?.id) {
        return;
      }
      setJob(result);
      setFormItemValue('keyTerms', normalizeKeyTerms(result.keyTerms ?? []));
      toast.success('Keyterms imported from extracted notices');
    },
    onSettled: (_data, _error, { jobId }) =>
      queryClient.invalidateQueries({ queryKey: jobsKeys.detail(jobId), refetchType: 'none' }),
  });
  const importingKeyTerms = importKeyTermsMutation.isPending;
  const keyTerms = normalizeKeyTerms(form.item?.keyTerms ?? []);
  const inputKeyTerms = splitKeyTerms(inputValue);
  const nextKeyTerms = [...keyTerms, ...inputKeyTerms];
  const keyTermsValidationError = inputValue
    ? getKeyTermsValidationError(nextKeyTerms)
    : getKeyTermsValidationError(keyTerms);
  const canAddKeyTerms = inputKeyTerms.length > 0 && !keyTermsValidationError;
  const importDisabled = noticeExtractionRunning || importingKeyTerms || !workspace?.id;

  const handleInputChange = (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInputValue(evt.target.value);
  };

  const addKeyTerms = async () => {
    if (!canAddKeyTerms) {
      return;
    }

    await setFormItemValue('keyTerms', nextKeyTerms);
    setInputValue('');
  };

  const deleteKeyTerm = async (indexToDelete: number) => {
    await setFormItemValue(
      'keyTerms',
      keyTerms.filter((_keyTerm, index) => index !== indexToDelete),
    );
  };

  const handleInputKeyUp = async (evt: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (evt.key === 'Enter') {
      await addKeyTerms();
    }
  };

  const importFromExtractedNotices = async () => {
    if (importDisabled) {
      return;
    }

    const ensuredJobId = await ensureDraftJob();

    if (!ensuredJobId || !workspace?.id) {
      toast.error('Something went wrong. Please check your internet connection and try again');
      return;
    }

    importKeyTermsMutation.mutate({ wsid: workspace.id, jobId: ensuredJobId });
  };

  return (
    <Block
      description="Allows you to provide up to 1,000 words or phrases (maximum 6 words per phrase) to improve transcription accuracy for those terms and related variations or contextually similar phrases."
      title="Keyterms"
    >
      <div className="flex flex-col items-start gap-3">
        {keyTerms.length ? <KeyTermsList keyTerms={keyTerms} onDelete={deleteKeyTerm} /> : null}

        <div className="flex w-full flex-col items-start gap-2">
          <Input
            onChange={handleInputChange}
            onKeyUp={handleInputKeyUp}
            name="keyTerm"
            label="Type keyterm"
            value={inputValue}
            error={!!keyTermsValidationError}
          />
          {keyTermsValidationError ? <ErrorMessage className="!text-left" error={keyTermsValidationError} /> : null}
          <div className="flex">
            <Button onClick={addKeyTerms} disabled={!canAddKeyTerms}>
              Add
            </Button>
            {!attachments.length ||
              (!attachments.some((attachment: JobDtoAttachment) => attachment.attachmentType !== 'NOTICE') && (
                <Button
                  onClick={importFromExtractedNotices}
                  disabled={importDisabled}
                  className="flex items-center gap-2 ml-2"
                >
                  Use notice keyterms
                  {(noticeExtractionRunning || importingKeyTerms) && <LoadingIcon className="h-4 w-4 animate-spin" />}
                </Button>
              ))}
          </div>
        </div>
      </div>
    </Block>
  );
};
